import { fetchPage } from '@drapejs/core';
import { Invoker, defineReceiver } from '@drapejs/invoker';
import { isNode } from '@drapejs/runtime-context';
import {
  configure,
  sales,
  batch,
  request,
  query,
} from '@motillo/drapejs-litium';
import * as user from '@/connectors/litium/user';
import * as gmgWebsites from '@/connectors/litium/websites';
import * as gmgSales from '@/connectors/litium/sales';
import * as giftcard from '@/connectors/litium/giftcard';
import * as giftCategory from '@/connectors/litium/giftCategory';
import * as search from '@/connectors/litium/search';
import * as giftcardFavorites from '@/connectors/litium/giftcardFavorites';
import * as giftshop from '@/connectors/litium/giftshop';

import { gql } from 'graphql-request';
import { buildUrlPathWithQuery } from './helpers/url-utils';

export default function (invoker: Invoker) {
  invoker
    .configureCommand(fetchPage)
    .addReceiver(
      defineReceiver(fetchPage, async function (command: any, data) {
        command.url = buildUrlPathWithQuery(
          `${(<any>command).protocol}//${(<any>command).host}${(<any>command).path
          }`,
          (<any>command).query
        );
        return data;
      })
    )
    .addReceiver(gmgWebsites.scheduleFetchPageReceiver)
    .addReceiver(
      defineReceiver(fetchPage, async function (command, data) {

        await this.invoke(batch.scheduleQuery, {
          query: gql`
            channel {
              systemId
              rootPath
              rootPathRedirectUrl
              displayName
              checkoutUrl
              receiptUrl
              orderTrackingPageUrl
              giftcardFavoritesPageUrl
              currency
              locale
              language
              googleTagManagerContainerId
              googleAnalyticsAccountId
              blackListedPostalCodes {
                countryCode
                region
                postalCodes {
                  rangeMin
                  rangeMax
                }
              }
              website {
                websiteTexts
                fields: websiteFields
              }
              websiteName
              headerLogotype
              footerLogotype
              favIcon
            }`,
          cacheKey: '__channel',
        });

        await this.invoke(batch.scheduleQuery, {
          query: gql`
            page(path: "/header") {
              fields
              blocksJson
            }`,
          cacheKey: '__header',
        });

        await this.invoke(batch.scheduleQuery, {
          query: gql`
            page(path: "/footer") {
              fields
              blocksJson
            }`,
          cacheKey: '__footer',
        });

        if (!isNode()) {
          await this.invoke(batch.scheduleQuery, {
            query: gql`cart { ...CartFields }`,
            fragments: sales.withCartFields(),
            cacheKey: '__cart',
          });

          await this.invoke(batch.scheduleQuery, {
            query: gql`
            activatedGiftCardsState (categorySystemId: $categorySystemId){
              giftCards {
                id
                type
                code
                countries {
                  code
                  name
                  region{
                    code
                  }
                }
                categoryIds
                checkoutAddresses {
                  collectionNo
                  code
                  name
                  name2
                  address
                  address2
                  postCode
                  city
                  country
                }
                balance
                inventory
              }
              firstCategory {
                categoryId
                url
              }
              favoriteProductIds
            }
          `,
            cacheKey: '__activeGiftCard',
            variables: {
              categorySystemId: {
                value: (await this.cache.getItem('__categoryId')) || '',
                type: 'String',
              },
            },
          });
        }

        await this.invoke(batch.scheduleQuery, {
          query: gql`
            countries {
              id
              channelId
              channelName
              name
              url
              availableLanguages {
                systemId
                languageId
                name
                isDefault
                current
              }
              flagUrl
              current
            }`,
          cacheKey: '__countrySwitcher'
        });
      })
    )
    .addReceiver(giftCategory.schedule)
    .addReceiver(batch.executeBathQueryReceiver);

  invoker
    .configureCommand(batch.scheduleQuery)
    .addReceiver(batch.scheduleQueryReceiver);

  invoker
    .configureCommand(sales.scheduleRefreshCart)
    .addReceiver(sales.scheduleRefreshCartReceiver);

  invoker
    .configureCommand(giftCategory.commands.loadAllSectionProducts)
    .addReceiver(giftCategory.receivers.loadAllSectionProducts);

  invoker
    .configureCommand(search.commands.searchGifts)
    .addReceiver(search.receivers.searchGifts);

  invoker
    .configureCommand(giftshop.commands.initSession)
    .addReceiver(giftshop.receivers.initSession);

  invoker.configureCommand(sales.addToCart).addReceiver(
    defineReceiver(
      sales.addToCart,
      async function (command, data) {
        const quantity = command.quantity || 1;
        const result = await request(
          this.cache,
          query(
            gql`
              mutation addToCart(
                $url: String!
                $articleNumber: String!
                $quantity: Decimal!
              ) {
                session(url: $url) {
                  addToCart {
                    addToCart(
                      articleNumber: $articleNumber
                      quantity: $quantity
                    ) {
                      error
                      row {
                        ...RowFields
                      }
                      cart {
                        ...CartFields
                      }
                    }
                  }
                }
              }
            `,
            ...sales.withCartFields()
          ),
          {
            url: command.url,
            articleNumber: command.articleNumber,
            quantity,
          }
        );
        await this.cache.setItem('__cart', result.data.session.addToCart.addToCart.cart);

        return {
          error: result.data.session.addToCart.addToCart.error,
          row: result.data.session.addToCart.addToCart.row,
        };
      },
      'litium'
    )
  );

  invoker
    .configureCommand(giftcard.commands.activateGiftCard)
    .addReceiver(giftcard.receivers.activateGiftCard);

  invoker
    .configureCommand(giftcard.commands.clearGiftCard)
    .addReceiver(giftcard.receivers.clearGiftCard);

  invoker
    .configureCommand(gmgSales.commands.addGiftToCart)
    .addReceiver(gmgSales.receivers.addGiftToCart);

  invoker
    .configureCommand(gmgSales.commands.placeGetMyGiftOrder)
    .addReceiver(gmgSales.receivers.placeGetMyGiftOrder);

  invoker
    .configureCommand(gmgSales.commands.addBackInStockAlert)
    .addReceiver(gmgSales.receivers.addBackInStockAlert);

  invoker
    .configureCommand(gmgSales.commands.getOrderTracking)
    .addReceiver(gmgSales.receivers.getOrderTracking);

  invoker
    .configureCommand(gmgSales.commands.getOrder)
    .addReceiver(gmgSales.receivers.getOrderReceiver);

  invoker
    .configureCommand(giftcardFavorites.commands.getGiftcardFavorites)
    .addReceiver(giftcardFavorites.receivers.getGiftcardFavorites);

  invoker
    .configureCommand(user.commands.changeCountry)
    .addReceiver(user.receivers.changeCountry);

  configure({
    fetchPage: 'title',
    cart: getCartFields(),
    cartRow: getCartRowFields(),
  });
}

function getCartFields() {
  return `
          isEmpty
          discountCode
          totalDeliveryCost
          totalDiscount
          totalOrderRow
          totalVat
          grandTotal
          countryId
          countryName
          currency {
            id
          }
          customerAddress {
            email
            firstName
            lastName
            address1
            address2
            city
            postalCode
            phone
            state
            country
            countryDisplayName
          }`;
}

function getCartRowFields() {
  return `
  id
  articleNumber
  description
  variantName
  baseProductName
  isAddressOptional
  quantity
  unitListPrice
  unitPrice
  campaignName
  imageId
  url
  variantBrand
  totalPrice`;
}
